import {
  WORKFLOW_CATALOGUE,
  WORKFLOW_REPEATS,
  WORKFLOW_SUBJECTS,
  type WorkflowFieldDef,
  type WorkflowInput,
  type WorkflowSubject,
  type WorkflowTrigger,
} from "@dentacore/shared-types";

import { RoleSelect } from "./box-panel";
import { EVENT_OPTIONS, triggerFor } from "./graph-model";
import type { WorkflowRefs } from "./use-workflow-refs";
import styles from "./workflows.module.css";

/**
 * **The workflow's details** (WF-D7): its name, what it runs on, how it starts,
 * where it runs, who sees it and whose permissions it runs with. In a panel on
 * the right that *Hide details* closes, so the canvas can take the width.
 */
export function DetailsPanel({
  draft,
  refs,
  canDelete,
  onChange,
  onSubject,
  onDelete,
}: {
  draft: WorkflowInput;
  refs: WorkflowRefs;
  canDelete: boolean;
  onChange: (draft: WorkflowInput) => void;
  /** A new record type, which the editor confirms when steps would be lost. */
  onSubject: (subject: WorkflowSubject) => void;
  onDelete: () => void;
}): React.JSX.Element {
  const def = WORKFLOW_CATALOGUE[draft.subject];
  const trigger = draft.trigger;
  const ring = trigger.kind === "ring";
  const setTrigger = (next: WorkflowTrigger): void => onChange({ ...draft, trigger: next });
  const columnFields = def.fields.filter((f) => f.column !== undefined);

  const toggleRole = (id: string): void =>
    onChange({
      ...draft,
      roleIds: draft.roleIds.includes(id)
        ? draft.roleIds.filter((r) => r !== id)
        : [...draft.roleIds, id],
    });
  const toggleBranch = (id: string): void =>
    onChange({
      ...draft,
      branchIds: draft.branchIds.includes(id)
        ? draft.branchIds.filter((b) => b !== id)
        : [...draft.branchIds, id],
    });

  return (
    <section className={styles.panelSection} data-testid="wf-details">
      <label className={styles.field}>
        <span className={styles.fieldLabel}>Name</span>
        <input
          className={`${styles.input} ${styles.nameInput}`}
          type="text"
          maxLength={120}
          value={draft.name}
          onChange={(event) => onChange({ ...draft, name: event.target.value })}
          data-testid="wf-name"
        />
      </label>

      <label className={styles.field}>
        <span className={styles.fieldLabel}>Runs on</span>
        <select
          className={styles.input}
          value={draft.subject}
          onChange={(event) => onSubject(event.target.value as WorkflowSubject)}
          data-testid="wf-subject"
        >
          {WORKFLOW_SUBJECTS.map((s) => (
            <option key={s} value={s}>
              {WORKFLOW_CATALOGUE[s].title}
            </option>
          ))}
        </select>
      </label>

      <label className={styles.field}>
        <span className={styles.fieldLabel}>Starts</span>
        <select
          className={styles.input}
          value={trigger.kind}
          onChange={(event) =>
            setTrigger(triggerFor(event.target.value as WorkflowTrigger["kind"], draft.subject))
          }
          data-testid="wf-trigger-kind"
        >
          <option value="ring">From the quick ring</option>
          <option value="event">When something happens</option>
          <option value="schedule">On a schedule</option>
        </select>
      </label>

      {trigger.kind === "event" && (
        <>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>When {def.label}</span>
            <select
              className={styles.input}
              value={trigger.on}
              onChange={(event) =>
                setTrigger({ ...trigger, on: event.target.value as typeof trigger.on })
              }
              data-testid="wf-event-on"
            >
              {EVENT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          {columnFields.length > 0 && (
            <label className={styles.field}>
              <span className={styles.fieldLabel}>Only when</span>
              <select
                className={styles.input}
                value={trigger.field ?? ""}
                onChange={(event) => {
                  const field = columnFields.find((f) => f.key === event.target.value);
                  setTrigger({
                    ...trigger,
                    field: field?.key ?? null,
                    value: field === undefined ? null : firstValue(field),
                  });
                }}
                data-testid="wf-event-field"
              >
                <option value="">Any change</option>
                {columnFields.map((f) => (
                  <option key={f.key} value={f.key}>
                    {f.label} becomes…
                  </option>
                ))}
              </select>
            </label>
          )}
          {trigger.field !== null && trigger.field !== undefined && (
            <BecomesValue
              field={columnFields.find((f) => f.key === trigger.field) ?? null}
              value={trigger.value ?? null}
              onChange={(value) => setTrigger({ ...trigger, value })}
            />
          )}
        </>
      )}

      {trigger.kind === "schedule" && (
        <>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Repeats</span>
            <select
              className={styles.input}
              value={trigger.repeat}
              onChange={(event) =>
                setTrigger({ ...trigger, repeat: event.target.value as typeof trigger.repeat })
              }
              data-testid="wf-schedule-repeat"
            >
              {WORKFLOW_REPEATS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>At</span>
            <input
              className={styles.input}
              type="time"
              value={trigger.at}
              onChange={(event) => setTrigger({ ...trigger, at: event.target.value })}
              data-testid="wf-schedule-at"
            />
          </label>
          {def.windows !== undefined && def.windows.length > 0 && (
            <label className={styles.field}>
              <span className={styles.fieldLabel}>Over</span>
              <select
                className={styles.input}
                value={trigger.window ?? def.windows[0]?.value ?? ""}
                onChange={(event) => setTrigger({ ...trigger, window: event.target.value })}
                data-testid="wf-schedule-window"
              >
                {def.windows.map((w) => (
                  <option key={w.value} value={w.value}>
                    {w.label}
                  </option>
                ))}
              </select>
            </label>
          )}
        </>
      )}

      <fieldset className={styles.fieldset}>
        <legend className={styles.fieldLabel}>Runs at</legend>
        <label className={styles.check}>
          <input
            type="checkbox"
            checked={draft.allBranches}
            onChange={(event) => onChange({ ...draft, allBranches: event.target.checked })}
            data-testid="wf-all-branches"
          />
          Every location
        </label>
        {!draft.allBranches &&
          (refs.branches ?? []).map((branch) => (
            <label key={branch.id} className={styles.check}>
              <input
                type="checkbox"
                checked={draft.branchIds.includes(branch.id)}
                onChange={() => toggleBranch(branch.id)}
                data-testid={`wf-branch-${branch.id}`}
              />
              {branch.name}
            </label>
          ))}
      </fieldset>

      <fieldset className={styles.fieldset}>
        <legend className={styles.fieldLabel}>For</legend>
        <div className={styles.roleChips}>
          {(refs.roles ?? []).map((role) => (
            <button
              key={role.id}
              type="button"
              className={draft.roleIds.includes(role.id) ? styles.roleOn : styles.roleChip}
              aria-pressed={draft.roleIds.includes(role.id)}
              onClick={() => toggleRole(role.id)}
              data-testid={`wf-role-${role.id}`}
            >
              {role.name}
            </button>
          ))}
        </div>
        <span className={styles.hint}>
          {draft.roleIds.length === 0
            ? "Nobody picked: every role sees it"
            : "These roles see it on the Workflows page"}
          {ring ? ` and in their quick ring on ${def.label}.` : "."}
        </span>
      </fieldset>

      <label className={styles.field}>
        <span className={styles.fieldLabel}>Runs as</span>
        {ring ? (
          <span className={styles.hint}>
            Whoever starts it from the ring, with their own permissions.
          </span>
        ) : (
          <>
            <RoleSelect
              refs={refs}
              value={draft.runAsRoleId}
              onChange={(runAsRoleId) => onChange({ ...draft, runAsRoleId })}
              testId="wf-run-as"
            />
            <span className={styles.hint}>
              Nobody is at the screen, so it does only what this role may do at each location.
            </span>
          </>
        )}
      </label>

      <label className={styles.check}>
        <input
          type="checkbox"
          checked={draft.enabled}
          onChange={(event) => onChange({ ...draft, enabled: event.target.checked })}
          data-testid="wf-enabled"
        />
        On — switched off, it starts no new runs
      </label>

      <p className={styles.hint}>
        {ring
          ? `Shows in the quick ring when someone Ctrl + clicks ${def.label}. Steps that run on their own go straight through; a step someone does waits for them to say it is done.`
          : "Runs by itself. A step someone does becomes a task in Tasks, for the role on its box. Workflows never message patients."}
      </p>

      {canDelete && (
        <div className={styles.panelFoot}>
          <button
            type="button"
            className={styles.dangerLink}
            onClick={onDelete}
            data-testid="wf-delete"
          >
            Delete this workflow
          </button>
        </div>
      )}
    </section>
  );
}

function firstValue(field: WorkflowFieldDef): string | number {
  if (field.type === "enum") return field.options?.[0]?.value ?? "";
  if (field.type === "number" || field.type === "money") return 0;
  if (field.type === "bool") return "true";
  return "";
}

/** *Becomes* — compared with the column's value as text, so a yes/no is "true" or "false". */
function BecomesValue({
  field,
  value,
  onChange,
}: {
  field: WorkflowFieldDef | null;
  value: string | number | null;
  onChange: (value: string | number) => void;
}): React.JSX.Element | null {
  if (field === null) return null;
  const label = <span className={styles.fieldLabel}>Becomes</span>;
  if (field.type === "enum")
    return (
      <label className={styles.field}>
        {label}
        <select
          className={styles.input}
          value={typeof value === "string" ? value : ""}
          onChange={(event) => onChange(event.target.value)}
          data-testid="wf-event-value"
        >
          {field.options?.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
    );
  if (field.type === "bool")
    return (
      <label className={styles.field}>
        {label}
        <select
          className={styles.input}
          value={String(value ?? "true")}
          onChange={(event) => onChange(event.target.value)}
          data-testid="wf-event-value"
        >
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      </label>
    );
  if (field.type === "number" || field.type === "money")
    return (
      <label className={styles.field}>
        {label}
        <input
          className={styles.input}
          type="number"
          step="any"
          value={typeof value === "number" ? value : ""}
          onChange={(event) => onChange(Number(event.target.value) || 0)}
          data-testid="wf-event-value"
        />
      </label>
    );
  return (
    <label className={styles.field}>
      {label}
      <input
        className={styles.input}
        type="text"
        value={typeof value === "string" ? value : ""}
        onChange={(event) => onChange(event.target.value)}
        data-testid="wf-event-value"
      />
    </label>
  );
}
